import { useId } from 'react';

import { EVIDENCE_BADGES } from '../../data/report';
import type { EvidenceBadgeId } from '../../data/report';
import './EvidenceBadge.css';

interface EvidenceBadgeProps {
  /** Which of the three. There is no fourth, and no caller-supplied label. */
  id: EvidenceBadgeId;
  className?: string;
}

/**
 * The badge that says how a finding was established: Arithmetic, Audit
 * precedent, or Guidance warning. Blue for the first, amber for the other
 * two — there is no third accent, and the variants are never told apart by
 * colour alone: each carries its own words.
 *
 * The one-line definition from COPY.md is shown on hover and on focus. It is
 * rendered twice on purpose — once as a visual panel that is aria-hidden, and
 * once for assistive technology through aria-describedby — because a tooltip
 * that is animated into view cannot also be the accessible description
 * without disappearing from the accessibility tree while it is hidden.
 */
export default function EvidenceBadge({ id, className }: EvidenceBadgeProps) {
  const badge = EVIDENCE_BADGES[id];
  const defId = useId();

  const classes = ['ebadge', `ebadge--${badge.tone}`, className]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={classes}>
      {/* Focusable so a keyboard reader can reach the definition, and so the
          hover affordance has a focus equivalent. */}
      <span className="ebadge__label" tabIndex={0} aria-describedby={defId}>
        {badge.label}
      </span>
      <span className="ebadge__def" aria-hidden="true">
        {badge.definition}
      </span>
      <span className="ebadge__sr" id={defId}>
        {badge.definition}
      </span>
    </span>
  );
}
